import { ProductSKU, StoreService } from 'jigsaw';
import { HStack, Text, VStack } from 'ui_2';
import { ArcadeTicketIcon } from '@components/ArcadeTicketIcon';
import { Link } from '@tanstack/react-router';
import { Route as product_sku_ad_route } from '@routes/$service/$productSKU/ad';
import { Route as index_route } from '@routes/index';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import { useDisplayFlag } from '@src/hooks/useArcadeStore';
import { useViewTransition } from '@src/hooks/useViewTransition';

type Props = {
  sku: ProductSKU;
  service: StoreService;
};

export const ArcadeStoreProductView = ({ sku, service }: Props) => {
  const { account } = useArcadeStoreState();
  const displayFlag = useDisplayFlag({ sku });

  return (
    <VStack className='w-full rounded-[10px] bg-white shadow-[0px_2px_10px_0px_rgba(0,0,0,0.25)] hover:bg-bg1 md:w-[190px] md:max-w-[200px] lg:w-[235px] lg:max-w-[250px] '>
      <div className='relative'>
        <img
          src={sku.bigImageURL ?? service.smallLogoImageURL}
          alt={`${service.title} logo`}
          className='h-[155px] w-full rounded-t-[10px] object-cover md:h-[190px] lg:h-[235px]'
        />
        <HStack className='absolute left-0 top-0 items-center gap-2 ps-2 pt-2'>
          {/* Require users to connect wallet before seeing stock */}
          {account && sku.stock === 0 && (
            <Text className='rounded-full bg-white px-2' font='sm'>
              SOLD OUT
            </Text>
          )}
          {sku.proOnly && (
            <Text className='rounded-full bg-secondary-yellow px-2' font='sm'>
              PRO
            </Text>
          )}
        </HStack>
      </div>
      <VStack className='p-2 lg:px-4'>
        <Text
          font='medium-sm'
          className='min-h-[2.5rem] text-left leading-tight'
        >
          {sku.title}
        </Text>
        <HStack className='w-full items-center justify-between'>
          <HStack className='gap-1'>
            <ArcadeTicketIcon />
            <Text font='sm'>{sku.price}</Text>
          </HStack>
          {displayFlag && (
            <Text className='h-5 text-[20px]'>{displayFlag}</Text>
          )}
        </HStack>
      </VStack>
    </VStack>
  );
};

export const ArcadeStoreProduct = ({ sku, service }: Props) => {
  const { slideLeftFullPage } = useViewTransition();

  return (
    <Link
      className='self-start'
      to={product_sku_ad_route.to}
      from={index_route.fullPath}
      params={{ productSKU: sku.skuId.toString(), service: service.slug }}
      viewTransition={slideLeftFullPage}
    >
      <ArcadeStoreProductView sku={sku} service={service} />
    </Link>
  );
};
